import {
  ArrowLeftOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useMsal } from "@azure/msal-react";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import {
  Button,
  Input,
  Layout,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Typography,
  message,
} from "antd";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ClinicianFormModal from "../components/features/clinicians/ClinicianModal";
import { Header } from "../components/layout/header";
import adminService from "../services/AdminService";
import practiceService from "../services/PracticeService";
import { useNavigationBlocker } from "../hooks/useNavigationBlocker";
import NavigationConfirmModal from "../components/common/NavigationConfirmModal";

const { Text } = Typography;

const roleList = ["admin", "clinician"];

const handleDownloadTemplate = (e) => {
  e?.preventDefault();
  e?.stopPropagation();

  const link = document.createElement("a");
  link.href = "/templates/clinicians_bulk_import_template.csv";
  link.download = "clinicians_bulk_import_template.csv";
  document.body.appendChild(link);
  link.click();
  link.remove();
};

const ManageClinicians = ({
  selectedDoctorId,
  setSelectedDoctorId,
  options,
}) => {
  const { instance } = useMsal();
  const navigate = useNavigate();

  const logout = () => {
    instance.logout();
    sessionStorage.clear();
  };

  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [practices, setPractices] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    practiceService
      .listPractices()
      .then((rows) => setPractices(Array.isArray(rows) ? rows : []))
      .catch(() => setPractices([]));
  }, []);

  // Navigation blocker for bulk import
  const {
    showConfirmation,
    blockingReason,
    handleConfirmLeave,
    handleCancelLeave,
  } = useNavigationBlocker({
    recordingParent: false,
    notes: '',
    generatingNotes: false,
    uploading: isImporting,  // Block navigation during bulk import
    pollingNote: false,
    fetchingNote: false,
    isNoteSigned: false,
    noteLocked: false,
    onBeforeLeave: async () => {
      // Log that user is leaving during import
      console.log('User navigating away during bulk import');
    },
  });

  const fetchUsers = async (page = 1, pageSize = 50, searchTerm = search, role = roleFilter) => {
    setLoading(true);
    try {
      const data = await adminService.getUsers({
        page,
        page_size: pageSize,
        search: searchTerm || undefined,
        role: role || undefined,
      });

      const apiUsers = Array.isArray(data?.users) ? data.users : [];

      const mappedUsers = apiUsers.map((u) => ({
        ...u,
        name: `${u.first_name || ""} ${u.last_name || ""}`.trim(),
        number: u.npi || u.clinician_id || "",
        is_active: u.is_active,
      }));

      setUsers(mappedUsers);

      const apiPagination = data?.pagination || {};
      setPagination({
        current: apiPagination.page || page,
        pageSize: apiPagination.page_size || pageSize,
        total: apiPagination.total_count || mappedUsers.length,
      });
    } catch (e) {
      console.error(e);
      message.error("Failed to load users.");
      setUsers([]);
      setPagination((prev) => ({
        ...prev,
        total: 0,
      }));
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchUsers(1, pagination.pageSize, "", null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Consolidated search + role effect with first-render guard so it does NOT
  // duplicate the initial load above. A ~300ms debounce is acceptable for both
  // the text input and the role Select (imperceptible for a Select click).
  const isFirstFilterRender = useRef(true);
  useEffect(() => {
    if (isFirstFilterRender.current) { isFirstFilterRender.current = false; return; }
    const t = setTimeout(() => fetchUsers(1, pagination.pageSize, search, roleFilter), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, roleFilter]);

  const handleAddClick = () => {
    setEditingUser(null);
    setIsModalOpen(true);
  };

  const handleEditClick = async (record) => {
    try {
      const user = await adminService.getUserById(record.id);
      setEditingUser(user || record);
      setIsModalOpen(true);
    } catch (e) {
      console.error(e);
      setEditingUser(record);
      setIsModalOpen(true);
    }
  };

  const handleDelete = async (id) => {
    try {
      await adminService.deactivateUser(id);
      fetchUsers(pagination.current, pagination.pageSize, search, roleFilter);
    } catch (e) {
      console.error(e);
      message.error("Failed to deactivate user.");
    }
  };

  const handleToggleActive = async (checked, record) => {
    // Optimistic update
    setUsers((prev) =>
      prev.map((u) => (u.id === record.id ? { ...u, is_active: checked } : u))
    );
    try {
      if (checked) {
        await adminService.reactivateUser(record.id);
      } else {
        await adminService.deactivateUser(record.id);
      }
    } catch (e) {
      console.error(e);
      // Revert on failure
      setUsers((prev) =>
        prev.map((u) => (u.id === record.id ? { ...u, is_active: !checked } : u))
      );
      message.error(checked ? "Failed to activate user." : "Failed to deactivate user.");
    }
  };

  const handleModalSubmit = async (values) => {
    try {
      if (editingUser && editingUser.id) {
        await adminService.updateUser(editingUser.id, values);
        message.success("User updated successfully.");
      } else {
        await adminService.createUser(values);
        message.success("User created successfully.");
      }

      await fetchUsers(pagination.current, pagination.pageSize, search, roleFilter);

      setIsModalOpen(false);
      setEditingUser(null);
    } catch (e) {
      console.error(e);
      message.error(
        editingUser ? "Failed to update user." : "Failed to create user."
      );
    }
  };

  const parseCsvToUsers = (csvText) => {
    const lines = csvText
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

    if (lines.length < 2) {
      return [];
    }

    const header = lines[0].split(",").map((h) => h.trim().toLowerCase());

    const getIndex = (name) =>
      header.findIndex((h) => h === name.toLowerCase());

    const emailIdx = getIndex("email");
    const amdProfileIdIdx = getIndex("amd_profile_id") >= 0
      ? getIndex("amd_profile_id")
      : getIndex("clinician_id");
    const npiIdx = getIndex("npi");
    const ssoIdIdx = getIndex("sso_id");
    const rolesIdx = getIndex("roles");
    const isActiveIdx = getIndex("is_active");
    const firstNameIdx = getIndex("first_name");
    const lastNameIdx = getIndex("last_name");
    const middleNameIdx = getIndex("middle_name");

    const result = [];

    for (let i = 1; i < lines.length; i++) {
      const parts = lines[i].split(",").map((p) => p.trim());

      const email = emailIdx >= 0 ? parts[emailIdx] || "" : "";
      if (!email) {
        continue;
      }

      const amdProfileIdRaw =
        amdProfileIdIdx >= 0 ? parts[amdProfileIdIdx] || "" : "";
      const amd_profile_id = amdProfileIdRaw ? parseInt(amdProfileIdRaw, 10) : null;

      const npi = npiIdx >= 0 ? parts[npiIdx] || "" : "";
      const sso_id = ssoIdIdx >= 0 ? parts[ssoIdIdx] || "" : "";

      let first_name = firstNameIdx >= 0 ? parts[firstNameIdx] || "" : "";
      let last_name = lastNameIdx >= 0 ? parts[lastNameIdx] || "" : "";
      const middle_name = middleNameIdx >= 0 ? parts[middleNameIdx] || "" : "";

      const rolesRaw = rolesIdx >= 0 ? parts[rolesIdx] || "" : "";
      const roles =
        rolesRaw.length > 0
          ? rolesRaw
              .split(/[;|]/)
              .map((r) => r.trim())
              .filter((r) => r.length > 0)
          : ["clinician"];

      const isActiveRaw =
        isActiveIdx >= 0 ? (parts[isActiveIdx] || "").toLowerCase() : "";
      const is_active =
        isActiveRaw === "true" || isActiveRaw === "1" || isActiveRaw === "yes";

      result.push({
        email,
        amd_profile_id: amd_profile_id && !Number.isNaN(amd_profile_id) ? amd_profile_id : null,
        npi: npi || null,
        sso_id: sso_id || null,
        roles,
        is_active,
        first_name: first_name || null,
        last_name: last_name || null,
        middle_name: middle_name || null,
      });
    }

    return result;
  };

  const handleBulkImportClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const handleBulkFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (!file.name.toLowerCase().endsWith(".csv")) {
      message.error("Please upload a CSV file.");
      return;
    }

    setIsImporting(true);

    try {
      const text = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = (e) => reject(e);
        reader.readAsText(file);
      });

      const payload = parseCsvToUsers(text || "");

      if (!payload.length) {
        message.warning("No valid rows found in CSV.");
        setIsImporting(false);
        return;
      }

      await adminService.bulkImportUsers(payload);
      message.success("Bulk import completed successfully.");
      await fetchUsers(pagination.current, pagination.pageSize, search, roleFilter);
    } catch (e) {
      console.error(e);
      message.error("Failed to import users from CSV.");
    } finally {
      setIsImporting(false);
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: "Email",
      dataIndex: "email",
      responsive: ["md"],
    },
    {
      title: "NPI Number",
      dataIndex: "number",
      responsive: ["lg"],
    },
    {
      title: "Active",
      dataIndex: "is_active",
      width: 100,
      render: (_, record) => (
        <Switch
          checked={record.is_active}
          onChange={(checked) => handleToggleActive(checked, record)}
        />
      ),
    },
    {
      title: "Roles",
      dataIndex: "roles",
      render: (roles = []) => (
        <div className="flex gap-2 flex-wrap">
          {roles.map((r) => (
            <span
              key={r}
              className="bg-slate-100 text-slate-700 px-3 py-1 rounded-full text-xs"
            >
              {r}
            </span>
          ))}
        </div>
      ),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      width: 110,
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEditClick(record)}
            className="hover:text-blue-500"
          />
        </Space>
      ),
    },
  ];

  return (
    <Layout className="flex flex-col h-screen">
      <Header
        onLogout={logout}
        selectedDoctorId={selectedDoctorId}
        onSelectDoctor={setSelectedDoctorId}
        options={options}
      />
      <div className="p-6 bg-white rounded-lg shadow-sm">
        <div className="flex items-center justify-between mb-4 gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              {/*
               * No unsaved-changes guard here: this page has no page-level
               * dirty state. Intentional, not an oversight (cf. ActionBar.js
               * which does guard because it auto-saves in-session changes).
               */}
              <Button
                size="small"
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate("/")}
                aria-label="Back to main menu"
              >
                Back
              </Button>
              <h2 className="text-lg font-semibold text-slate-800">
                Clinician Management
              </h2>
            </div>
            <p className="text-sm text-slate-500">
              Manage your application Clinicians and access.
            </p>
          </div>
          <div className="flex gap-2 items-start">
            <div className="flex flex-col items-end leading-tight gap-2">
              {/* Search + role filter (server-side) */}
              <div className="flex gap-2">
                <Input.Search
                  placeholder="Search by email…"
                  allowClear
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onSearch={(val) => setSearch(val)}
                  style={{ width: 220 }}
                  aria-label="Search clinicians"
                />
                <Select
                  value={roleFilter}
                  onChange={(val) => setRoleFilter(val)}
                  style={{ width: 130 }}
                  placeholder="All roles"
                  allowClear
                  aria-label="Filter by role"
                  options={[
                    { label: "Clinician", value: "clinician" },
                    { label: "Admin", value: "admin" },
                  ]}
                />
              </div>
              {/* Action buttons */}
              <div className="flex gap-2">
                <div className="flex items-center gap-2">
                  <Tooltip
                    placement="bottom"
                    title={
                      <div className="max-w-[260px]">
                        <div className="text-xs">
                          Use the template to match required columns and
                          formatting.
                        </div>
                        <Button
                          size="small"
                          icon={<DownloadOutlined />}
                          onClick={handleDownloadTemplate}
                        >
                          CSV Template
                        </Button>
                      </div>
                    }
                  >
                    <InfoCircleOutlined className="text-slate-400 hover:text-slate-600 cursor-pointer" />
                  </Tooltip>
                  <Button
                    onClick={handleBulkImportClick}
                    loading={isImporting}
                    className="flex items-center"
                  >
                    Bulk Import
                  </Button>
                </div>

                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddClick}
                >
                  Add Clinician
                </Button>
              </div>
            </div>
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          size="middle"
          className="rounded-md"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            onChange: (page, pageSize) => fetchUsers(page, pageSize, search, roleFilter),
          }}
        />

        <ClinicianFormModal
          open={isModalOpen}
          onCancel={() => {
            setIsModalOpen(false);
            setEditingUser(null);
          }}
          onSubmit={handleModalSubmit}
          initialValues={editingUser}
          roles={roleList}
          practices={practices}
          mode={editingUser ? "edit" : "add"}
        />

        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          style={{ display: "none" }}
          onChange={handleBulkFileChange}
        />
      </div>

      {/* Navigation Confirmation Modal */}
      <NavigationConfirmModal
        visible={showConfirmation}
        blockingReason="bulk_import"
        onConfirm={handleConfirmLeave}
        onCancel={handleCancelLeave}
      />
    </Layout>
  );
};

export default ManageClinicians;
